import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { BriefcaseBusiness, Loader2, SearchX } from "lucide-react";
import { Temporal } from "temporal-polyfill";
import { JobPostAvailability } from "@api/shared-runtime/enums";
import { useTRPC } from "@/trpc";

interface JobUnavailableStateProps {
  jobId: string;
  /** Page-specific call to action(s), rendered under the message. */
  actions: ReactNode;
  /** Called if the post turns out to be open again, so the page can refetch it. */
  onReopened: () => void;
}

interface UnavailableMessageProps {
  icon: ReactNode;
  heading: string;
  children: ReactNode;
  actions: ReactNode;
}

const NOT_FOUND_HEADING = "This job isn't available";
const NOT_FOUND_BODY =
  "The link may be out of date, or the job may have been removed. New opportunities are posted on Saile every day.";
const CLOSED_HEADING = "This job is no longer accepting applications";
const CLOSED_BODY =
  "The hiring team has closed this position. New opportunities are posted on Saile every day, so there may be a great fit waiting for you.";

function formatClosedDate(closedAt: string): string {
  return Temporal.Instant.from(closedAt)
    .toZonedDateTimeISO(Temporal.Now.timeZoneId())
    .toLocaleString("en-US", { dateStyle: "long" });
}

function UnavailableMessage({
  icon,
  heading,
  children,
  actions,
}: UnavailableMessageProps) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-4 py-12 min-h-[400px]">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
        {icon}
      </div>
      <h1 className="text-xl font-semibold text-slate-800 mb-2">{heading}</h1>
      {children}
      <div className="mt-6 flex w-full max-w-sm flex-col gap-3 sm:w-auto sm:max-w-none sm:flex-row">
        {actions}
      </div>
    </div>
  );
}

/** The message for a job that can't be shown, without asking why: for a job
 * that is missing, or whose page failed to load. */
export function JobNotAvailableMessage({ actions }: { actions: ReactNode }) {
  return (
    <UnavailableMessage
      icon={<SearchX className="h-7 w-7" aria-hidden="true" />}
      heading={NOT_FOUND_HEADING}
      actions={actions}
    >
      <p className="text-slate-500 max-w-md">{NOT_FOUND_BODY}</p>
    </UnavailableMessage>
  );
}

export function JobUnavailableState({
  jobId,
  actions,
  onReopened,
}: JobUnavailableStateProps) {
  const trpc = useTRPC();
  // A lookup that fails shows the not-found message; retrying only delays it.
  const { data, isLoading } = useQuery(
    trpc.jobPost.getAvailability.queryOptions({ id: jobId }, { retry: false }),
  );
  const isReopened = data?.status === JobPostAvailability.Open;

  useEffect(() => {
    if (isReopened) {
      onReopened();
    }
  }, [isReopened, onReopened]);

  if (isLoading || isReopened) {
    return (
      <div
        role="status"
        className="flex justify-center items-center min-h-[400px]"
      >
        <Loader2 className="h-10 w-10 animate-spin text-slate-400" />
        <span className="sr-only">Loading job</span>
      </div>
    );
  }

  if (data?.status !== JobPostAvailability.Closed) {
    return <JobNotAvailableMessage actions={actions} />;
  }

  return (
    <UnavailableMessage
      icon={<BriefcaseBusiness className="h-7 w-7" aria-hidden="true" />}
      heading={CLOSED_HEADING}
      actions={actions}
    >
      <div className="mb-3">
        <p className="text-base font-medium text-slate-700 max-w-md">
          {data.title}
        </p>
        {data.closedAt && (
          <p className="text-sm text-slate-500">
            Closed on {formatClosedDate(data.closedAt)}
          </p>
        )}
      </div>
      <p className="text-slate-500 max-w-md">{CLOSED_BODY}</p>
    </UnavailableMessage>
  );
}
